import { PaymentType } from '@/features/graphql/generated/graphql'
import { useOpenAccountSettlement, useOpenTabs } from '@/features/openTabs/contexts/OpenTabsData'
import { CommitAccountPaymentInput, SupportedAccountPaymentMethod } from '@/features/openTabs/types'
import {
    getAccountPaymentItemTotals,
    getAccountPaymentTotal,
    getAccountPriceBreakdown,
    isFullAccountSelection,
    isValidAccountSelection,
} from '@/features/openTabs/utils/accountCalculations'
import {
    formatAccountListPrice,
    formatAccountPrice,
    toAccountItemView,
    toAccountPriceItems,
} from '@/features/openTabs/utils/viewModels'
import { usePreventRemove } from '@react-navigation/native'
import currencyJs from 'currency.js'
import { useRouter } from 'expo-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const useAccountPayment = () => {
    const { t } = useTranslation('app')
    const router = useRouter()
    const {
        selectedAccount,
        detailLoading,
        detailFailed,
        detailRefreshFailed,
        listFailed,
        paymentMethods,
    } = useOpenTabs()
    const {
        paymentSelection,
        paymentSelectionMode,
        selectedPaymentItems,
        clearPaymentSelection,
        settleAccount,
        settlingAccount,
    } = useOpenAccountSettlement()
    const [selectedMethod, setSelectedMethod] = useState<SupportedAccountPaymentMethod>()
    const [calculatorOpen, setCalculatorOpen] = useState(false)
    const [calculatorHelper, setCalculatorHelper] = useState(false)
    const [receivedAmountInput, setReceivedAmountInput] = useState('')
    const [submissionPending, setSubmissionPending] = useState(false)
    const [submissionFailed, setSubmissionFailed] = useState(false)
    const [paymentCompleted, setPaymentCompleted] = useState(false)
    const [cancelOpen, setCancelOpen] = useState(false)
    const [cancelApproved, setCancelApproved] = useState(false)
    const cancelActionRef = useRef<(() => void) | undefined>(undefined)
    const cancelHandledRef = useRef(false)
    const submittingRef = useRef(false)
    const submitting = submissionPending || settlingAccount

    const paymentMethod =
        selectedMethod && paymentMethods.includes(selectedMethod)
            ? selectedMethod
            : paymentMethods[0]
    const paymentTotal = getAccountPaymentTotal(selectedAccount, paymentSelection)
    const receivedAmount = currencyJs(receivedAmountInput.replace(',', '.')).value
    const cashDifference = currencyJs(receivedAmount).subtract(paymentTotal)
    const isCovered = cashDifference.intValue >= 0
    const canCalculate =
        !detailLoading &&
        !detailFailed &&
        !detailRefreshFailed &&
        !listFailed &&
        (paymentSelectionMode === 'all'
            ? isFullAccountSelection(selectedAccount, paymentSelection)
            : isValidAccountSelection(selectedAccount, paymentSelection)) &&
        !submitting
    const canPay = canCalculate && !!paymentMethod

    usePreventRemove(
        !paymentCompleted && !cancelApproved && (submitting || calculatorOpen || cancelOpen),
        () => {
            if (submittingRef.current || submitting) return

            setCalculatorOpen(false)
            setSubmissionFailed(false)
            setCancelOpen(false)
        }
    )

    useEffect(() => {
        if (paymentCompleted) router.dismissTo('/open-tabs')
    }, [paymentCompleted, router])

    useEffect(() => {
        if (!cancelApproved || cancelHandledRef.current) return

        cancelHandledRef.current = true
        clearPaymentSelection()
        if (cancelActionRef.current) cancelActionRef.current()
        else router.dismissTo('/open-tabs')
    }, [cancelApproved, clearPaymentSelection, router])

    const paymentItemTotals = useMemo(
        () => getAccountPaymentItemTotals(selectedAccount, paymentSelection),
        [selectedAccount, paymentSelection]
    )
    const paymentSelectionKey = JSON.stringify({
        accountId: selectedAccount?.id,
        currency: selectedAccount?.currency,
        paymentMethod,
        paymentSelectionMode,
        items: selectedPaymentItems.map(({ item, quantity }) => [
            item.id,
            quantity,
            item.quantity,
            item.unitPrice,
        ]),
    })

    useEffect(() => {
        setCalculatorOpen(false)
        setReceivedAmountInput('')
        setSubmissionFailed(false)
    }, [paymentSelectionKey])

    const submitPayment = async (input: CommitAccountPaymentInput): Promise<boolean> => {
        if (submittingRef.current || !canPay || !selectedAccount) return false

        submittingRef.current = true
        setSubmissionPending(true)
        setSubmissionFailed(false)

        try {
            const settled = await settleAccount(selectedAccount.id, input)

            if (!settled) {
                setCalculatorOpen(false)
                setReceivedAmountInput('')
                clearPaymentSelection()
                setSubmissionFailed(true)
                return false
            }

            setCalculatorOpen(false)
            setPaymentCompleted(true)

            return true
        } catch {
            setCalculatorOpen(false)
            setReceivedAmountInput('')
            clearPaymentSelection()
            setSubmissionFailed(true)
            return false
        } finally {
            submittingRef.current = false
            setSubmissionPending(false)
        }
    }

    const handlePay = async () => {
        if (submittingRef.current || calculatorOpen || cancelOpen || !canPay || !paymentMethod)
            return

        if (paymentMethod === PaymentType.Cash) {
            setCalculatorHelper(false)
            setReceivedAmountInput('')
            setSubmissionFailed(false)
            setCalculatorOpen(true)
            return
        }

        await submitPayment({ method: paymentMethod })
    }

    const handleCashConfirm = async () => {
        if (calculatorHelper || !calculatorOpen || !isCovered || paymentMethod !== PaymentType.Cash)
            return

        await submitPayment({ method: PaymentType.Cash, receivedAmount })
    }

    const handleCashCancel = () => {
        if (submittingRef.current || submitting) return

        setCalculatorOpen(false)
        setSubmissionFailed(false)
    }

    const handleOpenCalculator = () => {
        if (submittingRef.current || !canCalculate) return

        setCalculatorHelper(true)
        setReceivedAmountInput('')
        setCalculatorOpen(true)
    }

    const handleCancel = (onConfirmed?: () => void) => {
        if (submittingRef.current || submitting) return

        cancelActionRef.current = onConfirmed
        setCancelOpen(true)
    }

    const handleCancelConfirm = () => {
        if (submittingRef.current || submitting) return

        setCancelOpen(false)
        setCancelApproved(true)
    }

    const handleCancelKeep = () => setCancelOpen(false)

    const handlePaymentMethodChange = (method: SupportedAccountPaymentMethod) => {
        if (submittingRef.current || submitting || !paymentMethods.includes(method)) return

        setSelectedMethod(method)
        setSubmissionFailed(false)
    }

    const handleDigit = (digit: string) => {
        if (submittingRef.current || submitting || !/^[\d,]$/.test(digit)) return

        setReceivedAmountInput((current) => {
            if (digit === ',' && current.includes(',')) return current

            const candidate =
                digit === ',' && !current
                    ? '0,'
                    : current === '0' && digit !== ','
                      ? digit
                      : `${current}${digit}`

            return /^\d{0,7}(,\d{0,2})?$/.test(candidate) ? candidate : current
        })
    }

    const handleQuickAmount = (amount: number) => {
        if (submittingRef.current || submitting || !Number.isFinite(amount) || amount < 0) return

        setReceivedAmountInput((current) => {
            const nextAmount = currencyJs(current.replace(',', '.')).add(amount).value
            const nextInput = String(nextAmount).replace('.', ',')

            return /^\d{1,7}(,\d{1,2})?$/.test(nextInput) ? nextInput : current
        })
    }

    const handleBackspace = () => {
        if (submittingRef.current || submitting) return

        setReceivedAmountInput((current) => current.slice(0, -1))
    }

    const errorMessage =
        detailFailed || detailRefreshFailed || listFailed
            ? t('openTabs.payment.accountUnavailable')
            : !detailLoading && !paymentMethod
              ? t('openTabs.payment.methodsUnavailable')
              : submissionFailed
                ? t('openTabs.payment.failed')
                : undefined
    const accountCurrency = selectedAccount?.currency ?? ''

    return {
        selectedAccount,
        summaryItems: selectedPaymentItems.map(({ item, quantity }) => ({
            ...toAccountItemView({ ...item, quantity }, t),
            totalPrice: formatAccountPrice(paymentItemTotals[item.id], item.currency),
        })),
        paymentTotalPrice: formatAccountPrice(paymentTotal, accountCurrency),
        paymentPriceItems: toAccountPriceItems(
            getAccountPriceBreakdown(selectedAccount, paymentSelection),
            accountCurrency,
            t
        ),
        paymentMethods,
        paymentMethod,
        submitting,
        canPay,
        canCalculate,
        paymentErrorMessage: errorMessage,
        calculator: {
            open: calculatorOpen,
            helper: calculatorHelper,
            totalPrice: formatAccountPrice(paymentTotal, accountCurrency),
            receivedPrice: formatAccountListPrice(receivedAmount, accountCurrency),
            remainingPrice: formatAccountPrice(Math.abs(cashDifference.value), accountCurrency),
            isCovered,
            canConfirm:
                !calculatorHelper && canPay && paymentMethod === PaymentType.Cash && isCovered,
            errorMessage,
        },
        handlePay,
        handleCancel,
        cancelOpen,
        handleCancelConfirm,
        handleCancelKeep,
        handleCashConfirm,
        handleCashCancel,
        handleOpenCalculator,
        handlePaymentMethodChange,
        handleDigit,
        handleQuickAmount,
        handleBackspace,
    }
}

export default useAccountPayment
